/**
 * The lines of a contents page: what each says, where it leads, what it is set in.
 *
 * Derived from the document each time it is asked for and never stored, for the
 * reason `contentsOf` gives: a contents page kept beside the document goes
 * quietly wrong the first time somebody renames a chapter. What a heading says
 * about itself (a label of its own, a colour, a line of detail, whether it is
 * listed at all) is held by the heading; how a level is set (its type, its
 * size) is the charter's and is chosen by the contents page.
 *
 * Pages are counted from the page breaks. A document made of pages (an
 * import, or one set out by hand) says exactly where each one starts; text
 * that flows is cut into pages only when it is printed, and until then there
 * is nothing to count, so no page numbers are given.
 *
 * Pure. A document in, lines out.
 */

import { isAccent } from '@/domain/layout/colour'
import type { Section } from '@/domain/section'

/** One line of the contents page. */
export interface ContentsRow {
  readonly id: string
  readonly label: string
  /** 1 is a chapter. */
  readonly level: number
  /** The heading's number where the document is numbered, else empty. */
  readonly number: string
  /** The page it is on, counting the document's page breaks; null when it has none. */
  readonly page: number | null
  /** How many pages it runs, to the next line of its level or above; null with `page`. */
  readonly pages: number | null
  /** The colour a chapter is set in: its own, or the palette's next. Null: the page's own. */
  readonly colour: string | null
  /** The author's line under the entry, or empty. */
  readonly detail: string
}

export interface ContentsOptions {
  /** The charter's colours, which chapters take in turn. */
  readonly palette: readonly string[]
  /** Heading numbers by section id, as the report works them out. */
  readonly numbers: ReadonlyMap<string, string>
}

type Step =
  | { readonly kind: 'break' }
  | {
      readonly kind: 'entry'
      readonly id: string
      readonly label: string
      readonly level: number
      readonly colour: string | null
      readonly detail: string
    }

/** The document in reading order: its entries, and where its pages start. */
function stepsOf(sections: readonly Section[], depth: number, level: number): Step[] {
  const steps: Step[] = []

  for (const section of sections) {
    if (section.kind === 'pageBreak') {
      steps.push({ kind: 'break' })
      continue
    }

    if (section.kind === 'group') {
      if (section.showTitle && level <= depth) {
        steps.push({
          kind: 'entry',
          id: section.id,
          label: section.title,
          level,
          colour: null,
          detail: '',
        })
      }
      steps.push(...stepsOf(section.children, depth, level + 1))
      continue
    }

    if (section.kind === 'heading' && section.level <= depth && !section.hideInContents) {
      steps.push({
        kind: 'entry',
        id: section.id,
        label: section.contentsLabel?.trim() ? section.contentsLabel.trim() : section.title,
        level: section.level,
        colour: section.colour && isAccent(section.colour) ? section.colour.toLowerCase() : null,
        detail: section.contentsDetail?.trim() ?? '',
      })
    }
  }

  return steps
}

/** The contents page's lines, down to `depth` levels. */
export function contentsRows(
  sections: readonly Section[],
  depth: number,
  options: ContentsOptions,
): ContentsRow[] {
  const steps = stepsOf(sections, depth, 1)
  const entries = steps.filter((step) => step.kind === 'entry')

  const breaks = steps.filter((step) => step.kind === 'break').length
  // A document opening on a page break counts it as its first page, not as an extra one.
  const first = steps[0]?.kind === 'break' ? 0 : 1
  const total = breaks + first

  // The page each entry is on: the page breaks before it, counted from the first page.
  const pageOf = new Map<string, number>()
  let seen = 0
  for (const step of steps) {
    if (step.kind === 'break') seen += 1
    else pageOf.set(step.id, seen + first)
  }

  let chapters = 0
  return entries.map((entry, at) => {
    const page = breaks > 0 ? (pageOf.get(entry.id) ?? null) : null

    // Runs to the next line at its level or above, or to the end of the document.
    const next = entries.slice(at + 1).find((other) => other.level <= entry.level)
    const end = next ? (pageOf.get(next.id) ?? total + 1) : total + 1
    const pages = page === null ? null : Math.max(1, end - page)

    let colour = entry.colour
    if (entry.level === 1) {
      const turn = chapters
      chapters += 1
      colour ??=
        options.palette.length > 0 ? (options.palette[turn % options.palette.length] ?? null) : null
    } else {
      colour = null
    }

    return {
      id: entry.id,
      label: entry.label,
      level: entry.level,
      number: options.numbers.get(entry.id) ?? '',
      page,
      pages,
      colour,
      detail: entry.detail,
    }
  })
}
